import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useBoolean,
} from '@chakra-ui/react';
import { INTERACTIVE_INFOGRAPH_WRAPPER } from 'constants/interactiveElement';
import { Code } from 'constants/keyboard';
import { INTERACTION_TRIGGERED } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { useAppSelector } from 'modules/Editor/store';
import { selectAllPageBackgroundData } from 'modules/Editor/store/infographSelector';
import { selectActivePage } from 'modules/Editor/store/pageSelector';
import { KeyboardEvent, ReactElement, ReactNode, cloneElement, useEffect, useRef, useState } from 'react';
import { WidgetType } from 'types/widget.types';
import { isLight } from 'utils/color';
import { CHART_WIDGET_TYPES } from 'widgets/Widget.config';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { getBoundingRectOfRotatedWidget } from 'widgets/WidgetLayout.helpers';

import { useWidget } from '../useWidget';

interface ExpandInteractionWrapperProps {
  children: ReactNode;
  modalContent: ReactNode;
}

/**
 * EXPAND INTERACTION WRAPPER
 *
 * Wrapper component that expands the children into a modal when clicked.
 * Supports keyboard interaction - modal is opened when the Enter key is pressed and the widget is focused.
 * Currently, only supports expanding the same widget that is clicked.
 */
export const ExpandInteractionWrapper = ({ children, modalContent }: ExpandInteractionWrapperProps) => {
  const [isModalOpen, setIsModalOpen] = useBoolean(false);

  // Get the container element for the modal - for private link, need to append to the infograph container in case of fscreen mode
  const getInfographRef = () => ({ current: document.getElementById(INTERACTIVE_INFOGRAPH_WRAPPER) || document.body });

  const openModal = () => {
    setIsModalOpen.on();

    Mixpanel.track(INTERACTION_TRIGGERED.eventName, {
      interaction_type: INTERACTION_TRIGGERED.interactionType.Expandable,
    });
  };

  // Open the modal when the Enter key is pressed
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === Code.Enter) {
      e.preventDefault();
      openModal();
    }
  };

  return (
    <>
      <Box
        w='full'
        h='full'
        pointerEvents='auto'
        onKeyDown={onKeyDown}
        tabIndex={0}
        cursor='pointer'
        onClick={openModal}
      >
        {children}
      </Box>
      <Modal
        isOpen={isModalOpen}
        onClose={setIsModalOpen.off}
        size='4xl'
        portalProps={{ containerRef: getInfographRef() }}
        autoFocus={false}
      >
        <ModalOverlay />
        <ExpandModalContent isOpen={isModalOpen}>{modalContent}</ExpandModalContent>
      </Modal>
    </>
  );
};

// Widgets in this list will have a variable background color depending on the page colour
const VARIABLE_BG_WIDGETS = [...CHART_WIDGET_TYPES, WidgetType.MapChart, WidgetType.QRCode];
const DARK_MODAL_COLOR = 'blackAlpha.900';
const LIGHT_MODAL_COLOR = 'whiteAlpha.900';
const MODAL_PADDING_PX = 32;

interface ExpandModalContentProps {
  children: ReactNode;
  isOpen: boolean;
}

const ExpandModalContent = ({ children, isOpen }: ExpandModalContentProps) => {
  const modalContentRef = useRef<HTMLDivElement>(null);
  const [elementScale, setElementScale] = useState(1);
  const activePage = useAppSelector(selectActivePage);
  const pageBackground = useAppSelector(selectAllPageBackgroundData(activePage));

  const { widthPx, heightPx, widgetId, rotateDeg } = useWidget();
  const widgetType = getWidgetTypeFromId(widgetId);

  const pageBgColor = pageBackground?.background || pageBackground?.gradientBackground?.colors?.[0];
  const isLightModal = VARIABLE_BG_WIDGETS.includes(widgetType) && pageBgColor && isLight(pageBgColor || '');
  const modalBg = isLightModal ? LIGHT_MODAL_COLOR : DARK_MODAL_COLOR;

  // Calculate the scale factor to fit the content inside the modal
  // Resize the content when the window is resized or the modal is opened
  useEffect(() => {
    const handleResize = () => {
      if (!isOpen) return;
      if (!modalContentRef.current) return;
      const { width, height } = modalContentRef.current.getBoundingClientRect();

      const { widthPx: rotatedWidth, heightPx: rotatedHeight } = getBoundingRectOfRotatedWidget(
        0,
        0,
        widthPx,
        heightPx,
        rotateDeg,
      );
      setElementScale(Math.min((width - MODAL_PADDING_PX) / rotatedWidth, (height - MODAL_PADDING_PX) / rotatedHeight));
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, widthPx, heightPx, rotateDeg]);

  if (!isOpen) return null;

  return (
    <ModalContent bg={modalBg}>
      <ModalBody h='80vh' flex='unset' p={4} overflow='hidden' ref={modalContentRef}>
        <Flex justifyContent='center' alignItems='center' w='full' h='full' transform={`scale(${elementScale})`}>
          <Box
            width={`${widthPx}px`}
            height={`${heightPx}px`}
            transform={`rotate(${rotateDeg}deg)`}
            position='relative'
          >
            {cloneElement(children as ReactElement)}
          </Box>
        </Flex>
      </ModalBody>
      <ExpandModalCloseButton mode={isLightModal ? 'light' : 'dark'} />
    </ModalContent>
  );
};

interface ExpandModalCloseButtonProps {
  mode: 'light' | 'dark';
}

const ExpandModalCloseButton = ({ mode }: ExpandModalCloseButtonProps) => (
  <ModalCloseButton
    position='absolute'
    color={mode === 'light' ? 'black' : 'white'}
    background={mode === 'light' ? LIGHT_MODAL_COLOR : DARK_MODAL_COLOR}
    borderRadius='50%'
    right='-2.5rem'
    top={0}
    _hover={{ bg: mode === 'light' ? 'hover.gray' : 'blackAlpha.700' }}
  />
);
